import { Check, Loader2, Trash2, UserPlus, X } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Card, Field, btnGhost, btnPrimary, inputCls } from '../components/ui';
import { ApiError } from '../lib/api';
import { formatStamp } from '../lib/format';
import { useSession } from '../lib/session';

interface Editor {
  userId: string;
  name: string;
  username?: string;
  addedAt: string;
  addedBy?: { email?: string };
}
interface AccessRequest {
  userId: string;
  name: string;
  username?: string;
  requestedAt: string;
}
interface Data {
  editors: Editor[];
  requests: AccessRequest[];
}

const errorText = (e: unknown) =>
  e instanceof ApiError
    ? ((e.body as { message?: string; error?: string }).message ?? e.message)
    : e instanceof Error
      ? e.message
      : String(e);

const who = (p: { name: string; username?: string }) =>
  p.username ? `${p.name} (@${p.username})` : p.name;

/**
 * Bot Telegram (solo admin, docs/07-admin-e-bot.md §3): chi può usare il bot in privato. Chi è
 * nella chat della redazione lo usa già; gli altri scrivono al bot, ricevono il loro ID e
 * compaiono qui tra le richieste.
 */
export function Telegram() {
  const { api, isAdmin } = useSession();
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      setData(await api<Data>('/telegram'));
    } catch (e) {
      setError(errorText(e));
    }
  };
  useEffect(() => {
    if (isAdmin) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin)
    return <main className="p-6 text-muted">La gestione del bot è riservata agli admin.</main>;

  const authorize = async (userId: string, name?: string) => {
    setBusy(userId);
    setError(null);
    setNotice(null);
    try {
      const r = await api<{ editor: Editor; welcomed: boolean }>(`/telegram/editors/${userId}`, {
        method: 'PUT',
        body: name ? { name } : {},
      });
      setNotice(
        r.welcomed
          ? `${who(r.editor)} è autorizzato: gli è arrivato il benvenuto con i comandi.`
          : `${who(r.editor)} è autorizzato. Non ha ancora avviato il bot: deve aprirlo e premere Avvia.`,
      );
      await load();
      return true;
    } catch (e) {
      setError(errorText(e));
      return false;
    } finally {
      setBusy(null);
    }
  };
  const remove = async (path: string, confirmText?: string) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setError(null);
    setNotice(null);
    try {
      await api(path, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <main className="mx-auto max-w-[1100px] p-4 md:p-6">
      <h1 className="font-display text-h2">Bot Telegram</h1>
      <p className="mt-1 max-w-[70ch] text-sm text-muted">
        Chi è nella chat della redazione usa già il bot. Qui autorizzi chi lo usa in privato: chi
        scrive al bot senza essere autorizzato riceve il suo ID e compare tra le richieste.
      </p>
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {notice && (
        <p role="status" className="mt-4 bg-ok-soft p-3 text-sm">
          {notice}
        </p>
      )}
      {!data ? (
        <p className="mt-4 text-sm text-muted">Caricamento…</p>
      ) : (
        <div className="mt-4 flex flex-col gap-6">
          {data.requests.length > 0 && (
            <Card title={`Richieste (${data.requests.length})`}>
              <ul className="flex flex-col divide-y divide-surface-strong">
                {data.requests.map((r) => (
                  <li key={r.userId} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                    <span className="min-w-0 flex-1">
                      <b>{who(r)}</b>
                      <span className="block text-muted">
                        ID {r.userId} · ha scritto {formatStamp(r.requestedAt)}
                      </span>
                    </span>
                    <button
                      onClick={() => void authorize(r.userId)}
                      disabled={busy !== null}
                      className={btnPrimary}
                    >
                      {busy === r.userId ? (
                        <Loader2 className="size-4 animate-spin" aria-hidden />
                      ) : (
                        <Check className="size-4" aria-hidden />
                      )}
                      Autorizza
                    </button>
                    <button
                      onClick={() => void remove(`/telegram/requests/${r.userId}`)}
                      className={btnGhost}
                    >
                      <X className="size-4" aria-hidden /> Ignora
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card title={`Redattori autorizzati (${data.editors.length})`}>
            {data.editors.length === 0 ? (
              <p className="text-sm text-muted">
                Nessuno: per ora il bot risponde solo nella chat della redazione.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-[11px] uppercase tracking-[0.04em] text-muted">
                  <tr>
                    <th className="py-2 pr-3 font-medium">Nome</th>
                    <th className="py-2 pr-3 font-medium">ID Telegram</th>
                    <th className="py-2 pr-3 font-medium">Aggiunto</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.editors.map((e) => (
                    <tr key={e.userId} className="border-t border-surface-strong">
                      <td className="py-2 pr-3">{who(e)}</td>
                      <td className="py-2 pr-3 tabular-nums">{e.userId}</td>
                      <td className="py-2 pr-3 text-muted">
                        {formatStamp(e.addedAt)}
                        {e.addedBy?.email ? ` · ${e.addedBy.email}` : ''}
                      </td>
                      <td className="py-2 text-right">
                        <button
                          onClick={() =>
                            void remove(
                              `/telegram/editors/${e.userId}`,
                              `Togliere a ${who(e)} l’accesso al bot?`,
                            )
                          }
                          className={btnGhost}
                        >
                          <Trash2 className="size-4" aria-hidden /> Togli
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <AddEditor busy={busy !== null} onAdd={authorize} />
        </div>
      )}
    </main>
  );
}

function AddEditor({
  busy,
  onAdd,
}: {
  busy: boolean;
  onAdd: (userId: string, name?: string) => Promise<boolean>;
}) {
  const [userId, setUserId] = useState('');
  const [name, setName] = useState('');
  const valid = /^\d{1,15}$/.test(userId.trim());
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    if (await onAdd(userId.trim(), name.trim() || undefined)) {
      setUserId('');
      setName('');
    }
  };
  return (
    <Card title="Aggiungi con l’ID">
      <form onSubmit={(e) => void submit(e)} className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
        <Field
          label="ID Telegram"
          hint="Un numero. Lo riceve chi scrive al bot senza essere autorizzato."
          error={userId && !valid ? 'Solo cifre' : undefined}
        >
          <input
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            inputMode="numeric"
            className={inputCls}
          />
        </Field>
        <Field label="Nome" hint="Facoltativo se ha già scritto al bot: lo prendiamo da Telegram.">
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        </Field>
        <div className="md:pt-5">
          <button type="submit" disabled={!valid || busy} className={btnPrimary}>
            <UserPlus className="size-4" aria-hidden /> Autorizza
          </button>
        </div>
      </form>
    </Card>
  );
}
